import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ISurvey, ITag } from 'src/redux/features/surveys/allSurvey/types';
import SurveyItem from '../SurveyItem/SurveyItem';
import styles from './styles.styl';
import cn from 'classnames/bind';
import { loadListSurvey } from 'src/redux/features/surveys/allSurvey/actions';
import {
  allSurveyLoadedSelector,
  allSurveyPendingSelector,
  allSurveyRejectedSelector,
  allSurveySelector,
} from 'src/redux/features/surveys/allSurvey/selectors';
import { State } from 'src/redux/reducer';
import { Loader } from '@consta/uikit/Loader';
import { TextField } from '@consta/uikit/TextField';
import { IconSearch } from '@consta/uikit/IconSearch';
import { IconFunnel } from '@consta/uikit/IconFunnel';
import { Button } from '@consta/uikit/Button';
import { Combobox } from '@consta/uikit/Combobox';
import {
  userInitLoadedSelector,
  userInitPendingSelector,
  userInitSelector,
} from 'src/redux/features/userInit/selectors';
import { IUserInitEnums } from 'src/redux/features/userInit/types';
import { SurveyStatusEnum, SurveyTypeEnum } from 'src/utils/enum';
import { loadListTag } from 'src/redux/features/surveys/allTag/actions';
import {
  allTagLoadedSelector,
  allTagPendingSelector,
  allTagRejectedSelector,
  allTagSelector,
} from 'src/redux/features/surveys/allTag/selectors';
import { duplicateSurveyLoadedSelector } from 'src/redux/features/surveys/duplicateSurvey/selectors';
import { duplicateSurveyIdle } from 'src/redux/features/surveys/duplicateSurvey/actions';

const cx = cn.bind(styles);

export interface ISurveyListProps {
  loadSurveys?: () => void;
  loadUserInit?: () => void;
  loadTags?: () => void;
  userInit?: IUserInitEnums;
  userInitLoaded?: boolean;
  userInitPending?: boolean;
  userInitRejected?: boolean;
  surveys?: ISurvey[];
  surveysLoaded?: boolean;
  surveysRejected?: boolean;
  surveysPending?: boolean;
  allTag?: ITag[];
  allTagLoaded?: boolean;
  allTagPending?: boolean;
  allTagRejected?: boolean;
  duplicateSurveyIdle?: () => void;
  duplicated?: boolean;
  setDeletedName: (name: string) => void;
}

export interface Item {
  label: string;
  id: string | number;
  action?: () => void;
  status?: SurveyStatusEnum;
  type?: SurveyTypeEnum;
  reverse?: boolean;
}

const SurveyList: React.FC<ISurveyListProps> = ({
  surveys,
  loadSurveys,
  userInit,
  surveysLoaded,
  surveysPending,
  userInitLoaded,
  loadTags,
  allTag,
  allTagLoaded,
  duplicated,
  duplicateSurveyIdle,
  setDeletedName,
  // allTagRejected,
  // userInitPending,
}) => {
  const [filteredList, setFilteredList] = useState<ISurvey[]>([]);
  const [isSortVisible, setIsSortVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string | null>(null);

  const [typeValue, setTypeValue] = useState<Item | null>(null);
  const [tagValue, setTagValue] = useState<Item[] | null>(null);
  const [statusValue, setStatusValue] = useState<Item | null>(null);
  const [dateValue, setDateValue] = useState<Item | null>(null);

  const [tags, setTags] = useState<Item[]>([]);

  useEffect(() => {
    loadSurveys();
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (duplicated) {
      loadSurveys();
      duplicateSurveyIdle();
    }
  }, [duplicated, duplicateSurveyIdle, loadSurveys]);

  useEffect(() => {
    if (surveysLoaded && allTagLoaded) {
      setFilteredList(surveys);
      setTags(allTag.map((tag) => ({ id: tag.id, label: tag.name })));
    }
  }, [surveysLoaded, surveys, allTagLoaded, allTag]);

  useEffect(() => {
    if (surveys) {
      const data = surveys.filter(
        (item) =>
          (!searchValue ||
            item?.name?.toLowerCase().includes(searchValue.toLowerCase())) &&
          (!statusValue ||
            statusValue?.status === undefined ||
            item.status === statusValue.status) &&
          (!typeValue ||
            typeValue?.type === undefined ||
            typeValue.type === item.type) &&
          (!tagValue ||
            !tagValue.length ||
            tagValue.some((v) => !!item.tags.find((tag) => tag.id === v.id))),
      );

      if (dateValue && dateValue?.reverse) {
        return setFilteredList(data.reverse());
      }
      setFilteredList(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, statusValue, typeValue, dateValue, tagValue, dateValue]);

  const sortCategoryItems: Item[] = [
    {
      label: 'Все категории',
      id: 'allCategory',
    },
    {
      label: 'Опросы',
      id: 'surveys',
      type: SurveyTypeEnum.SURVEY,
    },
    {
      label: 'Тесты',
      id: 'tests',
      type: SurveyTypeEnum.TEST,
    },
  ];

  const sortStatusItems: Item[] = [
    {
      label: 'Все статусы',
      id: 'allStatus',
    },
    {
      label: 'Черновики',
      id: 'draft',
      status: SurveyStatusEnum.DRAFT,
    },
    {
      label: 'Опубликованные',
      id: 'published',
      status: SurveyStatusEnum.PUBLISHED,
    },
    {
      label: 'Завершенные',
      id: 'ended',
      status: SurveyStatusEnum.ENDED,
    },
  ];

  const sortDateItems: Item[] = [
    {
      label: 'От новых к старым',
      id: 'newToOld',
      reverse: false,
    },
    {
      label: 'От старых к новым',
      id: 'oldToNew',
      reverse: true,
    },
  ];

  return (
    <div className={cx('SurveyList')}>
      <div className={cx('SurveyList__filters')}>
        <div className={cx('SurveyList__search')}>
          <TextField
            className={cx('SurveyList__search_input')}
            value={searchValue}
            onChange={({ value }) => setSearchValue(value)}
            placeholder="Найти опрос"
            leftSide={IconSearch}
            key="search"
          />
          <Button
            onlyIcon
            iconRight={IconFunnel}
            iconSize="s"
            view="clear"
            onClick={() => setIsSortVisible(!isSortVisible)}
          />
        </div>
        {isSortVisible && (
          <div className={cx('SurveyList__sort')}>
            <div>
              <Combobox
                className={cx('SurveyList__sort_select')}
                placeholder="Все категории"
                items={sortCategoryItems}
                onChange={({ value }) => setTypeValue(value)}
                value={typeValue}
              />
              <Combobox
                placeholder="Все статусы"
                items={sortStatusItems}
                onChange={({ value }) => setStatusValue(value)}
                value={statusValue}
              />
            </div>
            <div>
              <Combobox
                className={cx('SurveyList__sort_select')}
                placeholder="Все теги"
                items={tags}
                onChange={({ value }) => setTagValue(value)}
                value={tagValue}
                multiple
              />
              <Combobox
                placeholder="От новых к старым"
                items={sortDateItems}
                onChange={({ value }) => setDateValue(value)}
                value={dateValue}
              />
            </div>
          </div>
        )}
      </div>
      <div className={cx('SurveyList__list')}>
        {surveysPending && <Loader />}
        {surveysLoaded &&
          userInitLoaded &&
          allTagLoaded &&
          filteredList.map((survey) => (
            <SurveyItem
              survey={survey}
              key={survey.id}
              userInit={userInit}
              setDeletedName={setDeletedName}
            />
          ))}
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  loadSurveys: () => dispatch(loadListSurvey()),
  loadTags: () => dispatch(loadListTag()),
  duplicateSurveyIdle: () => dispatch(duplicateSurveyIdle()),
});

const mapStateToProps = (state: State, props: ISurveyListProps) => ({
  surveys: allSurveySelector(state),
  surveysLoaded: allSurveyLoadedSelector(state, props),
  surveysRejected: allSurveyRejectedSelector(state),
  surveysPending: allSurveyPendingSelector(state),
  userInitLoaded: userInitLoadedSelector(state, props),
  userInitPending: userInitPendingSelector(state),
  userInit: userInitSelector(state),
  allTag: allTagSelector(state),
  allTagLoaded: allTagLoadedSelector(state, props),
  allTagPending: allTagPendingSelector(state),
  allTagRejected: allTagRejectedSelector(state),
  duplicated: duplicateSurveyLoadedSelector(state, props),
});

export default connect(mapStateToProps, mapDispatchToProps)(SurveyList);
